/**
 * Shared Camera Kit warm-up, and the kiosk's readiness state.
 *
 * bootstrapCameraKit() downloads Snap's WASM — that is the slow part.
 * loadLens() downloads the Lens package — also slow. Both can run while the
 * guest is still on the title / attract screens so the capture step only has to
 * create a session, attach the camera, play, and apply the (already cached) Lens.
 *
 * ## Why there is a retry loop in here
 *
 * This used to be a bare promise kicked off from KioskFlow's mount effect, with
 * no retry: a failure nulled `warmPromise` and nothing called it again until the
 * guest reached capture, ~20 seconds later, where it failed a second time and
 * dropped them on an error screen. On a unit that boots before the venue network
 * is up — which is the normal order of events — that was every guest until
 * somebody power-cycled it.
 *
 * So warm-up is now a small state machine that retries with backoff and wakes on
 * `online`, and publishes what it is doing so the admin screen and the readiness
 * dot can show it.
 *
 * ## What this CANNOT fix
 *
 * `bootstrapCameraKit` authenticates the API token and `loadLens()` downloads
 * the Lens package on **every page load**. Camera Kit's IndexedDB holds remote
 * config, analytics and legal state — not lens content — and LensRepository's
 * cache is in-memory, per page. The only thing between a restart and a full
 * re-download is the WebView's evictable HTTP cache.
 *
 * So: the unit must be online at boot, and the shell must keep the WebView alive
 * between guests rather than reloading. See android/README.md.
 */

import { bootstrapCameraKit, estimateLensPerformance } from '@snap/camera-kit';
import { createBootstrapURLs } from '@snap/camera-kit/utils';
import type { CameraKit, Lens, EstimatedLensPerformance } from '@snap/camera-kit';
import { env } from '../config/env';
import { devLog, devWarn } from '../lib/devLog';
import { createStore, useStore } from '../lib/store';

const TAG = '[ar:camera-kit:warm]';

type WarmResult = { cameraKit: CameraKit; lens: Lens };

let warmPromise: Promise<WarmResult> | null = null;
/** Last successful result — used so a lens refresh can keep the same bootstrap. */
let warmCache: WarmResult | null = null;

/* -------------------------------------------------------------- warm status */

export type ArWarmState = 'idle' | 'warming' | 'ready' | 'failed';

export interface ArWarmStatus {
  state: ArWarmState;
  /** Completed attempts, successful or not. */
  attempts: number;
  lastError: string | null;
  lastAttemptAt: number | null;
  readyAt: number | null;
  /** Milliseconds the successful bootstrap+lens took. Null until ready. */
  durationMs: number | null;
}

export const arWarmStatus = createStore<ArWarmStatus>({
  state: 'idle',
  attempts: 0,
  lastError: null,
  lastAttemptAt: null,
  readyAt: null,
  durationMs: null,
});

export function useArWarmStatus(): ArWarmStatus {
  return useStore(arWarmStatus);
}

function patch(next: Partial<ArWarmStatus>): void {
  arWarmStatus.set((prev) => ({ ...prev, ...next }));
}

/* ------------------------------------------------------------------ warm-up */

/**
 * Where to fetch the rendering engine from.
 *
 * Unset (the default) means Snap's CDN, which is right for the web deploy.
 * Setting VITE_CK_WASM_ENDPOINT points it at a self-hosted copy in the standard
 * multi-flavor layout — `<endpoint>/<flavor>/LensCoreWebAssembly.{js,wasm}` —
 * which `scripts/fetch-lens-core.mjs` downloads. On the kiosk that removes the
 * largest single cold-start fetch (~6.5MB) from the critical path.
 *
 * `createBootstrapURLs` with no explicit flavor detects the right one for the
 * running browser, the same way the SDK does for its own CDN. So the endpoint
 * must hold every flavor the unit might pick, or bootstrap 404s — see the
 * script's --flavor note.
 */
async function bootstrapOptions(): Promise<{
  apiToken: string;
  lensPerformance: Promise<EstimatedLensPerformance>;
  lensCoreOverrideUrls?: { js: string; wasm: string };
}> {
  const base = { apiToken: env.ckApiToken, lensPerformance: ratePerformance() };
  if (!env.ckWasmEndpoint) return base;
  try {
    const lensCoreOverrideUrls = await createBootstrapURLs(env.ckWasmEndpoint);
    devLog(`${TAG} self-hosted lens core`, lensCoreOverrideUrls);
    return { ...base, lensCoreOverrideUrls };
  } catch (err) {
    // A bad endpoint must not be fatal — falling back to the CDN is slower but
    // works, and a kiosk that will not start at all is worse than a slow one.
    devWarn(`${TAG} lens core override failed, falling back to the CDN`, err);
    return base;
  }
}

/**
 * How fast this device is, in the SDK's terms: a cluster from 1 (slowest) to 6.
 *
 * Camera Kit hands this number to the lens, and a lens can scale its effects on
 * it. The SDK does not measure it unless asked — the default is 0, below the
 * slowest real tier, which is how PixelChefs' smoke went missing on two Macs
 * on 2026-09-20 while the costume itself rendered fine. The benchmark is a
 * short WebGL run, started here so it overlaps the WASM download; if it fails
 * the SDK falls back to its default rather than refusing to start.
 */
function ratePerformance(): Promise<EstimatedLensPerformance> {
  return estimateLensPerformance().then((p) => {
    devLog(`${TAG} lens performance cluster ${p.cluster}/6 (${p.webglRendererInfo})`);
    return p;
  });
}

export function warmCameraKit(): Promise<WarmResult> {
  if (env.arModule !== 'camera-kit') {
    return Promise.reject(new Error('Camera Kit warm requested but VITE_AR_MODULE is not camera-kit'));
  }
  if (!env.ckApiToken || !env.ckLensId || !env.ckLensGroupId) {
    return Promise.reject(new Error('Camera Kit env vars missing (token / lens id / group id)'));
  }

  if (warmPromise) return warmPromise;

  patch({ state: 'warming', lastAttemptAt: Date.now() });

  warmPromise = (async () => {
    const t0 = performance.now();
    devLog(`${TAG} bootstrap starting`);
    const cameraKit = await bootstrapCameraKit(await bootstrapOptions());
    devLog(`${TAG} bootstrap done in ${Math.round(performance.now() - t0)}ms`);

    const t1 = performance.now();
    devLog(`${TAG} loading lens`, env.ckLensId);
    const lens = await cameraKit.lensRepository.loadLens(env.ckLensId, env.ckLensGroupId);
    devLog(
      `${TAG} lens ready (${lens.name ?? lens.id}) in ${Math.round(performance.now() - t1)}ms`,
    );

    const result = { cameraKit, lens };
    warmCache = result;
    patch({
      state: 'ready',
      attempts: arWarmStatus.get().attempts + 1,
      lastError: null,
      readyAt: Date.now(),
      durationMs: Math.round(performance.now() - t0),
    });
    return result;
  })().catch((err) => {
    // Allow a later retry if the first warm failed (offline, bad token, etc.).
    warmPromise = null;
    warmCache = null;
    patch({
      state: 'failed',
      attempts: arWarmStatus.get().attempts + 1,
      lastError: err instanceof Error ? err.message : String(err),
    });
    throw err;
  });

  return warmPromise;
}

/* -------------------------------------------------------------- retry loop */

const FIRST_DELAY_MS = 2_000;
const MAX_DELAY_MS = 30_000;

let loopRunning = false;
let retryTimer: number | null = null;
let onlineHooked = false;

function clearRetry(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

/**
 * Keep trying until Camera Kit is ready, then stop.
 *
 * Idempotent, so every caller can just ask. Never rejects — failures land in
 * `arWarmStatus` where the admin screen and the readiness dot read them, which
 * is the whole point: on a kiosk nobody is watching a console.
 *
 * It retries forever by design. The show has to recover on its own whenever the
 * venue network comes back, whether that is in ten seconds or an hour.
 */
export function startArWarm(): void {
  if (env.arModule !== 'camera-kit') return;
  if (loopRunning) return;
  loopRunning = true;

  let delay = FIRST_DELAY_MS;

  const attempt = () => {
    clearRetry();
    warmCameraKit()
      .then(() => {
        loopRunning = false;
        clearRetry();
      })
      .catch(() => {
        // Already recorded in arWarmStatus by warmCameraKit's catch.
        devWarn(`${TAG} warm failed, retrying in ${delay}ms`);
        retryTimer = window.setTimeout(attempt, delay);
        delay = Math.min(delay * 2, MAX_DELAY_MS);
      });
  };

  if (!onlineHooked && typeof window !== 'undefined') {
    onlineHooked = true;
    window.addEventListener('online', () => {
      if (arWarmStatus.get().state === 'ready') return;
      devLog(`${TAG} back online — retrying immediately`);
      delay = FIRST_DELAY_MS;
      if (loopRunning) attempt();
      else startArWarm();
    });
  }

  attempt();
}

/** Operator action: force another attempt now, whatever the backoff says. */
export function retryArWarmNow(): void {
  clearRetry();
  loopRunning = false;
  invalidateCameraKitWarm();
  startArWarm();
}

/** Drop the warm cache so the next warmCameraKit() / refresh re-downloads. */
export function invalidateCameraKitWarm(): void {
  warmPromise = null;
  warmCache = null;
  patch({ state: 'idle', lastError: null, readyAt: null, durationMs: null });
  devLog(`${TAG} cache invalidated`);
}

/**
 * Re-download the Lens package (CDN checksum failures, truncated QUIC, etc.)
 * while keeping the already-bootstrapped CameraKit instance when possible.
 */
export async function refreshCameraKitLens(cameraKit?: CameraKit): Promise<WarmResult> {
  if (env.arModule !== 'camera-kit') {
    throw new Error('Camera Kit refresh requested but VITE_AR_MODULE is not camera-kit');
  }
  if (!env.ckLensId || !env.ckLensGroupId) {
    throw new Error('Camera Kit env vars missing (lens id / group id)');
  }

  const kit = cameraKit ?? warmCache?.cameraKit;
  if (!kit) {
    invalidateCameraKitWarm();
    return warmCameraKit();
  }

  const t0 = performance.now();
  devLog(`${TAG} refreshing lens`, env.ckLensId);
  const lens = await kit.lensRepository.loadLens(env.ckLensId, env.ckLensGroupId);
  devLog(`${TAG} lens refreshed (${lens.name ?? lens.id}) in ${Math.round(performance.now() - t0)}ms`);

  const result = { cameraKit: kit, lens };
  warmCache = result;
  warmPromise = Promise.resolve(result);
  patch({ state: 'ready', lastError: null, readyAt: Date.now() });
  return result;
}
